import { deleteCookie, readCookie } from "@/lib/cookie";

/**
 * Referral attribution carrier for the portal.
 *
 * The code is captured on apps/web (`pontimed.app/...?r=CODE`, see
 * `apps/web/src/lib/referral/use-referral-attribution.ts`), which stores it in
 * ITS OWN localStorage — unreadable from portal.pontimed.app — and mirrors it
 * into a cookie scoped to the parent `pontimed.<tld>`. That cookie is the only
 * thing that survives the hop to this app.
 *
 * We forward it explicitly in the signup body as well: the browser does send
 * a `.pontimed.<tld>` cookie to api.pontimed.app (same site, credentials:
 * include), but the Capacitor build runs from `file://` with no shared cookie
 * jar, and the body path covers both. Before any of this existed, nothing ever
 * wrote the cookie the API was reading, so `processReferralRegistration` never
 * ran for any signup — no UserReferral row was created, so no referrer was
 * credited with a sign-up, nor later with a commission on that user's plan.
 */
const REF_COOKIE = "pontimed_ref";

/**
 * Referral codes are 8-char lowercase alphanumerics
 * (apps/api `lib/referrals.ts#generateReferralCode`). Anything else is a stale
 * or tampered value and is dropped rather than sent to the API.
 */
function normalize(value: string | null): string | undefined {
  if (!value) return undefined;
  const code = value.trim().toLowerCase();
  return /^[a-z0-9]{4,32}$/.test(code) ? code : undefined;
}

/** The referral code this visitor arrived with, if any. */
export function getCapturedReferralCode(): string | undefined {
  return normalize(readCookie(REF_COOKIE));
}

/** Drop the code once it has been attributed, so a later signup on the same
 *  device (family member, shared browser) isn't credited to the same referrer. */
export function clearCapturedReferralCode(): void {
  deleteCookie(REF_COOKIE);
}
